import { useMemo } from 'react';
import {
  DESCRICAO_MODO_MOTIVO,
  DESCRICAO_TRACO,
  LADOS_MOTIVO,
  MODOS_MOTIVO,
  MOTIVOS_LINHA,
  ROTULO_ESCRITA,
  ROTULO_LADO_MOTIVO,
  ROTULO_MODO_MOTIVO,
  ROTULO_MOTIVO,
  ROTULO_TRACO,
  TRACOS_LINHA,
  cssDaPauta,
  motivosPorGrupo,
  type EstiloEscrita,
  type EstiloTraco,
  type LadoMotivo,
  type ModoMotivo,
  type MotivoLinha,
} from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Campo } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LinhaSwitch, Slider } from '@/components/ui/switch';
import { CartaoOpcao } from '@/components/CartaoOpcao';
import { GlifoMotivo } from '@/impressao/GlifoMotivo';
import { useEditor } from '../EstadoEditor';
import { AmostraCor, Grade, TituloPasso } from '../ui';

const ESTILOS: EstiloEscrita[] = ['LINHA', 'PONTILHADO', 'QUADRICULADO', 'PONTOS', 'SEM_LINHA'];

/**
 * Traço e desenhos das linhas.
 *
 * É aqui que a agenda ganha personalidade: linha ondulada com corações, pauta
 * dupla com gráficos, margem com patinhas... A prévia ao lado atualiza na hora.
 */
export function PassoLinhas() {
  const { config, paleta, definirEscrita } = useEditor();
  const escrita = config.escrita;

  const passo = Math.max(2.5, escrita.alturaLinha + escrita.espacamento);

  /** A mesma função que desenha a pauta impressa desenha a amostra abaixo. */
  const amostra = useMemo(
    () =>
      cssDaPauta(escrita, {
        linha: paleta.linha,
        destaque: escrita.corMotivo ?? paleta.destaque,
      }),
    [escrita, paleta.linha, paleta.destaque],
  );

  const temMotivo = (escrita.motivo ?? 'NENHUM') !== 'NENHUM';

  return (
    <div className="space-y-6">
      <TituloPasso
        titulo="Traço e desenhos das linhas"
        descricao="Além das linhas comuns, você pode escolher como o fio é desenhado e que desenho enfeita cada linha: corações, patinhas, dentinhos, esmaltes, gráficos... Vale para as anotações do dia e para os cadernos."
      />

      {/* Amostra fiel do que vai sair no papel */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Como vai ficar</h3>
          <span className="flex items-center gap-2">
            <Badge>{ROTULO_TRACO[escrita.traco ?? 'SOLIDA']}</Badge>
            {temMotivo ? <Badge tom="destaque">{ROTULO_MOTIVO[escrita.motivo ?? 'NENHUM']}</Badge> : null}
            {temMotivo ? <Badge>{ROTULO_MODO_MOTIVO[escrita.motivoModo ?? 'AO_LONGO_DA_LINHA']}</Badge> : null}
          </span>
        </div>

        <div
          className="rounded-md border border-border"
          style={{
            height: `${passo * 6}mm`,
            backgroundColor: paleta.fundo,
            ...amostra,
          }}
        />
        <p className="text-xs text-muted-foreground">
          Amostra em tamanho real ({escrita.alturaLinha} mm de linha), desenhada pelo mesmo motor que gera o PDF.
        </p>
      </section>

      {/* Estilo base */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Estilo de escrita</h3>
        <Grade colunas={3}>
          {ESTILOS.map((estilo) => (
            <CartaoOpcao
              key={estilo}
              selecionado={escrita.estilo === estilo}
              titulo={ROTULO_ESCRITA[estilo]}
              aoSelecionar={() => definirEscrita({ estilo })}
              marca={
                <div
                  className="h-5 w-full rounded-sm border border-border"
                  style={{
                    backgroundColor: paleta.fundo,
                    ...cssDaPauta(
                      { ...escrita, estilo, motivo: 'NENHUM' },
                      { linha: paleta.linha, destaque: paleta.destaque },
                    ),
                  }}
                />
              }
            />
          ))}
        </Grade>
      </section>

      {/* Traço */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Como desenhar o fio</h3>
        <Grade colunas={3}>
          {TRACOS_LINHA.map((traco: EstiloTraco) => (
            <CartaoOpcao
              key={traco}
              selecionado={(escrita.traco ?? 'SOLIDA') === traco}
              titulo={ROTULO_TRACO[traco]}
              descricao={DESCRICAO_TRACO[traco]}
              aoSelecionar={() => definirEscrita({ traco })}
              marca={
                <div
                  className="h-5 w-full rounded-sm border border-border"
                  style={{
                    backgroundColor: paleta.fundo,
                    ...cssDaPauta(
                      { ...escrita, estilo: 'LINHA', traco, motivo: 'NENHUM', alturaLinhaMm: 5 },
                      { linha: paleta.linha, destaque: paleta.destaque },
                    ),
                  }}
                />
              }
            />
          ))}
        </Grade>
      </section>

      {/* Desenhos */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Desenho das linhas</h3>
          <span className="text-xs text-muted-foreground">
            {temMotivo ? 'Clique em “Sem desenho” para voltar à pauta limpa.' : 'Escolha um desenho para enfeitar a pauta.'}
          </span>
        </div>

        {motivosPorGrupo().map(({ grupo, motivos }) => (
          <div key={grupo} className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{grupo}</p>
            <div className="flex flex-wrap gap-2">
              {motivos.map((motivo: MotivoLinha) => {
                const ativo = (escrita.motivo ?? 'NENHUM') === motivo;
                return (
                  <button
                    key={motivo}
                    type="button"
                    onClick={() => definirEscrita({ motivo })}
                    aria-pressed={ativo}
                    title={ROTULO_MOTIVO[motivo]}
                    className={[
                      'flex size-16 flex-col items-center justify-center gap-1 rounded-lg border transition-colors',
                      ativo ? 'border-primary bg-primary/[0.06]' : 'border-border bg-card hover:border-primary/40',
                    ].join(' ')}
                  >
                    <GlifoMotivo
                      motivo={motivo}
                      cor={ativo ? paleta.principal : paleta.textoSuave}
                      tamanhoMm={6}
                    />
                    <span className="max-w-full truncate px-1 text-[9px] leading-tight text-muted-foreground">
                      {ROTULO_MOTIVO[motivo]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* Ajustes do desenho */}
      {temMotivo ? (
        <section className="space-y-4 rounded-lg border border-border bg-card p-4">
          <h3 className="text-sm font-semibold">Como o desenho aparece</h3>

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo rotulo="Distribuição" dica={DESCRICAO_MODO_MOTIVO[escrita.motivoModo ?? 'AO_LONGO_DA_LINHA']}>
              <Select
                value={escrita.motivoModo ?? 'AO_LONGO_DA_LINHA'}
                onValueChange={(valor) => definirEscrita({ motivoModo: valor as ModoMotivo })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODOS_MOTIVO.map((modo) => (
                    <SelectItem key={modo} value={modo}>
                      {ROTULO_MODO_MOTIVO[modo]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>

            <Campo rotulo="Margem" dica="Vale quando o desenho fica um por linha.">
              <Select
                value={escrita.motivoLado ?? 'ESQUERDA'}
                onValueChange={(valor) => definirEscrita({ motivoLado: valor as LadoMotivo })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LADOS_MOTIVO.map((lado) => (
                    <SelectItem key={lado} value={lado}>
                      {ROTULO_LADO_MOTIVO[lado]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Tamanho do desenho</span>
              <span className="font-mono text-xs text-muted-foreground">{escrita.motivoTamanhoMm ?? 3.2} mm</span>
            </div>
            <Slider
              value={[escrita.motivoTamanhoMm ?? 3.2]}
              min={1.6}
              max={8}
              step={0.2}
              onValueChange={([valor]) => definirEscrita({ motivoTamanhoMm: valor ?? 3.2 })}
            />
          </div>

          {(escrita.motivoModo ?? 'AO_LONGO_DA_LINHA') === 'AO_LONGO_DA_LINHA' ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Distância entre um desenho e o próximo</span>
                <span className="font-mono text-xs text-muted-foreground">
                  {escrita.motivoEspacamentoMm ?? 20} mm
                </span>
              </div>
              <Slider
                value={[escrita.motivoEspacamentoMm ?? 20]}
                min={6}
                max={40}
                step={1}
                onValueChange={([valor]) => definirEscrita({ motivoEspacamentoMm: valor ?? 20 })}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Um desenho a cada quantas linhas</span>
                <span className="font-mono text-xs text-muted-foreground">
                  {escrita.motivoACadaNLinhas ?? 1}
                </span>
              </div>
              <Slider
                value={[escrita.motivoACadaNLinhas ?? 1]}
                min={1}
                max={4}
                step={1}
                onValueChange={([valor]) => definirEscrita({ motivoACadaNLinhas: valor ?? 1 })}
              />            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => definirEscrita({ corMotivo: null })}
              className={[
                'flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs',
                !escrita.corMotivo ? 'border-primary bg-primary/[0.06]' : 'border-border',
              ].join(' ')}
            >
              <AmostraCor cor={paleta.destaque} className="size-4" />
              Cor de destaque da paleta
            </button>
            {escrita.corMotivo ? (
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                <AmostraCor cor={escrita.corMotivo} className="size-4" />
                cor própria escolhida
              </span>
            ) : null}
            <LinhaSwitch
              id="cor-propria-desenho"
              rotulo="Usar uma cor própria no desenho"
              descricao="Deixe desligado para acompanhar a paleta."
              checked={Boolean(escrita.corMotivo)}
              onCheckedChange={(valor) =>
                definirEscrita({ corMotivo: valor ? paleta.principal : null })
              }
            />
          </div>
        </section>
      ) : null}

      <p className="rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
        Dica: os desenhos também aparecem nas <strong className="text-foreground">linhas de demarcação</strong> entre
        as seções da página — é o mesmo desenho, repetido sobre a linha.
      </p>
    </div>
  );
}
